import { useQueryGetShopifyCartOneTimeProductsV2 } from '@portal/state/cart';
import { useQueryGetShopifyCollectionProducts } from 'processes/SubscriptionPageV2/hooks/productsByCollections';
import { useMemo } from 'react';
import { useStoreValue } from 'state/hooks/useStore';
import { AG1_KEY, AGZ_KEY, isKeyInTitle, PRIORITY } from '../../SiteCart/constants';
import { useSiteCart } from '../../SiteCart/hooks/SiteCartContext';

type UseCartModalCarouselProductsV2 = {
  showScoopFirstInCarousel?: boolean;
};

const FLAVOR_KEY = 'flavor';
const FLAVOR_GROUP_KEY = 'flavor_group';
const getProductMetafield = (metafields: any, key: string) => {
  if (Array.isArray(metafields)) {
    return metafields.find(
      (metafield) => metafield?.namespace === 'custom' && metafield?.key === key
    )?.value;
  }
  if (typeof metafields === 'object') {
    return metafields?.[key];
  }
  return null;
};

export const useCartModalCarouselProductsV2 = ({
  showScoopFirstInCarousel = false
}: UseCartModalCarouselProductsV2) => {
  const store = useStoreValue();

  const { cart, setCart } = useQueryGetShopifyCartOneTimeProductsV2();
  const { allItems } = useSiteCart();
  const collectionHandle = 'ymal-prospects';

  const { data: carouselProducts = [], isPending: productsIsLoading } =
    useQueryGetShopifyCollectionProducts({
      storeCode: store,
      collectionHandle,
      identifiers: [
        { key: 'carousel_visible', namespace: 'custom' },
        { key: 'flavor', namespace: 'custom' },
        { key: 'flavor_group', namespace: 'custom' }
      ]
    });

  const processedCarouselProducts = useMemo(() => {
    if (!carouselProducts.length) return [];

    // Filter out existing cart items
    const filteredProducts = carouselProducts.filter((product) => {
      // Filter by matching ID
      const isIdInCart = allItems.some((cartProduct) =>
        product.variants.find((variantId) => variantId.id === cartProduct.merchandise.id)
      );

      // Filter by matching flavor + group
      const productFlavor =
        getProductMetafield(product.variants[0]?.metafields, FLAVOR_KEY) ||
        getProductMetafield(product.metafields, FLAVOR_KEY);
      const productFlavorGroup =
        getProductMetafield(product.variants[0]?.metafields, FLAVOR_GROUP_KEY) ||
        getProductMetafield(product.metafields, FLAVOR_GROUP_KEY);
      const hasValidFlavor = !!productFlavor && !!productFlavorGroup;
      const isSameFlavorGroupInCart =
        hasValidFlavor &&
        allItems.some((cartProduct) => {
          const cartProductFlavor =
            getProductMetafield(cartProduct.merchandise.metafields, FLAVOR_KEY) ||
            getProductMetafield(cartProduct.merchandise.product.metafields, FLAVOR_KEY);
          const cartProductFlavorGroup =
            getProductMetafield(cartProduct.merchandise.metafields, FLAVOR_GROUP_KEY) ||
            getProductMetafield(cartProduct.merchandise.product.metafields, FLAVOR_GROUP_KEY);

          return (
            cartProductFlavorGroup === productFlavorGroup && cartProductFlavor === productFlavor
          );
        });

      return !isIdInCart && !isSameFlavorGroupInCart;
    });

    /*
     * Sort by following rules:
     *
     * Base Order
     * 1. AG1
     * 2. AGZ
     * 3. Omega (OTP)
     * 4. D3 (OTP)
     *
     * If AG1 in cart, show remaining AG1 flavors last
     * If AGZ in cart, show remaining AGZ flavors last
     * If AG1 / AGZ in cart, show all remaining AG1 / AGZ flavors last
     */

    if (showScoopFirstInCarousel) {
      // Out of scope
    }

    const isAG1InCart = allItems.some((cartProduct) =>
      isKeyInTitle(AG1_KEY, cartProduct.merchandise.product.title)
    );
    const isAGZInCart = allItems.some((cartProduct) =>
      isKeyInTitle(AGZ_KEY, cartProduct.merchandise.product.title)
    );

    const sortedProducts = [...filteredProducts].sort((a, b) => {
      // Identify AG1 / AGZ
      const itemAIsAG1 = isKeyInTitle(AG1_KEY, a.title);
      const itemBIsAG1 = isKeyInTitle(AG1_KEY, b.title);
      const itemAIsAGZ = isKeyInTitle(AGZ_KEY, a.title);
      const itemBIsAGZ = isKeyInTitle(AGZ_KEY, b.title);
      // Deprioritize AGZ if in cart
      if (isAGZInCart && itemAIsAGZ && !itemBIsAGZ) return 1;
      if (isAGZInCart && itemBIsAGZ && !itemAIsAGZ) return -1;
      // Deprioritize AG1 if in cart
      if (isAG1InCart && itemAIsAG1 && !itemBIsAG1) return 1;
      if (isAG1InCart && itemBIsAG1 && !itemAIsAG1) return -1;

      // Sort by standard priority
      const itemAKey = Object.keys(PRIORITY).find((key) => isKeyInTitle(key, a.title)) || '';
      const itemBKey = Object.keys(PRIORITY).find((key) => isKeyInTitle(key, b.title)) || '';
      const priorityA = PRIORITY[itemAKey as keyof typeof PRIORITY] || 0;
      const priorityB = PRIORITY[itemBKey as keyof typeof PRIORITY] || 0;

      return priorityA - priorityB;
    });

    return sortedProducts;
  }, [allItems, carouselProducts, showScoopFirstInCarousel]);

  return {
    products: processedCarouselProducts,
    productsIsLoading,
    cart,
    setCart
  };
};
